import type { SoundPlayer } from '../SoundPlayer'
import type { PayloadInjector } from '../tracker'
import type { AudioStreamBlobEvent } from './AudioStreamEventTarget'
import { AudioStreamManager } from './AudioStreamManager'
import { InvalidRecorderStatus } from './Error'
import type { NetworkCongestionEvent } from './NetworkManager'
import { NetworkManager } from './NetworkManager'
import { ForceStopUploadQueueError } from './UploadQueue'

export type RecorderStatus = 'idle' | 'recording' | 'paused' | 'stopped'

export interface RecorderProcessConfig {
  millisecondsPerChunk: number
  maxRecordingSeconds: number
}

export class RecorderProcess extends EventTarget {
  status: RecorderStatus = 'idle'

  #duration: number = 0
  readonly #streamManager: AudioStreamManager
  readonly #networkManager: NetworkManager
  readonly #soundPlayer: SoundPlayer
  readonly #config: RecorderProcessConfig
  readonly #abortController: AbortController
  readonly #payloadInjector: PayloadInjector

  constructor(
    soundPlayer: SoundPlayer,
    payloadInjector: PayloadInjector,
    config: RecorderProcessConfig
  ) {
    super()
    this.#abortController = new AbortController()
    this.#soundPlayer = soundPlayer
    this.#payloadInjector = payloadInjector
    this.#config = config
    this.#streamManager = new AudioStreamManager({
      timeslice: this.#config.millisecondsPerChunk,
      audioBitsPerSecond: 128000
    })
    this.#networkManager = new NetworkManager(this.#payloadInjector)
  }

  async start() {
    if (this.status !== 'idle') {
      throw new InvalidRecorderStatus('start', this.status)
    }

    this.status = 'recording'
    this.#setup()
    await this.#streamManager.start()
    this.#prevSampleTime = Date.now()
    this.#networkManager.register()
  }

  resume() {
    if (this.status !== 'paused') {
      throw new InvalidRecorderStatus('resume', this.status)
    }

    this.status = 'recording'
    this.#streamManager.resume()
    this.#prevSampleTime = Date.now()
  }

  async stop() {
    if (!(this.status === 'recording' || this.status === 'paused')) {
      throw new InvalidRecorderStatus('stop', this.status)
    }

    this.#soundPlayer.playFinish()
    this.status = 'stopped'
    this.#streamManager.stop()
    const [finalizeResponse, blobs] = await this.#networkManager.finalize()
    this.#teardown()
    finalizeResponse.moteStatus.duration = this.#duration
    return [finalizeResponse, blobs] as const
  }

  async pause() {
    if (this.status !== 'recording') {
      throw new InvalidRecorderStatus('pause', this.status)
    }

    this.status = 'paused'
    this.#streamManager.pause()
    await this.#networkManager.pause()
  }

  async cancel(playSound: boolean = true) {
    if (!(this.status === 'paused' || this.status === 'recording')) {
      throw new InvalidRecorderStatus('cancel', this.status)
    }

    this.status = 'idle'
    if (playSound) {
      this.#soundPlayer.playCancel()
    }
    this.#teardown()
    this.#streamManager.cancel()
    return this.#networkManager.cancel()
  }

  get stream() {
    return this.#streamManager.mediaStream
  }

  fetchMote() {
    return this.#networkManager.fetchMote()
  }

  #nextTick: ((duration: number) => void) | null = null
  nextTick(cb: (duration: number) => void) {
    this.#nextTick = cb
  }

  #setup() {
    this.#streamManager.addEventListener(
      'dataavailable',
      this.#dataAvailableHandler,
      { signal: this.#abortController.signal }
    )
    this.#networkManager.addEventListener(
      'error',
      this.#networkManagerErrorHandler,
      { signal: this.#abortController.signal }
    )
    this.#networkManager.addEventListener(
      'networkcongestion',
      this.#networkManagerNetworkCongestionHandler,
      { signal: this.#abortController.signal }
    )
  }

  #teardown() {
    this.#nextTick = null
    this.#abortController.abort('teardown')
  }

  #prevSampleTime: number = 0
  #dataAvailableHandler = {
    handleEvent: (event: AudioStreamBlobEvent) => {
      const isPausedStateOnSafari =
        event.data.size === 0 && this.status === 'paused'

      if (isPausedStateOnSafari || this.status === 'stopped') {
        return
      }

      if (event.data.size === 0) {
        return
      }

      const now = Date.now()
      const sampleTime = now - this.#prevSampleTime
      this.#prevSampleTime = now

      this.#duration += sampleTime
      const seconds = Math.round(this.#duration / 1000)

      if (event.data.size !== 0) {
        this.#networkManager.send(event.data)
      }

      if (this.#nextTick) {
        this.#nextTick(seconds)
      }

      if (seconds >= this.#config.maxRecordingSeconds) {
        this.dispatchEvent(new Event('timelimit'))
      }
    }
  }

  #networkManagerErrorHandler = {
    handleEvent: (event: ErrorEvent) => {
      if (event.error instanceof ForceStopUploadQueueError) {
        this.dispatchEvent(new Event('timelimit'))
      } else {
        this.status = 'idle'
        this.#teardown()
        this.#streamManager.teardown()
        this.dispatchEvent(new ErrorEvent('error', event))
      }
    }
  }

  #networkManagerNetworkCongestionHandler = {
    handleEvent: (event: NetworkCongestionEvent) => {
      this.dispatchEvent(new CustomEvent('networkcongestion', event))
    }
  }
}
